import * as THREE from 'three'

/**
 * テクスチャを URL 単位で共有するキャッシュ。
 *
 * 同一 URL を参照する部品が数百個あっても（RJS02 のメッシュは 1 画像を最大 414 回、
 * 壁 decal は 1 画像を 399 面で参照）、Texture を 1 つに集約することで GPU への重複
 * アップロード（texSubImage2D）を排除し、シーン開始時のメインスレッドブロックを抑える。
 *
 * Texture の設定は configure に、生ロード（TextureLoader 等）は loadRaw に委ねることで、
 * 本体を three の副作用から切り離してテスト可能にしている。variant は同一 URL でも設定を
 * 分けたい軸（メッシュ=カットアウト有無、decal=タイル繰り返し有無）を表し、キーと
 * configure の双方へ渡される。
 */
export interface SharedTextureCache {
  /** url(+variant) 単位で共有 Texture を返す。同一キーは同じ Promise を共有する。 */
  load: (url: string, variant: boolean) => Promise<THREE.Texture>
  /** キャッシュ済みの全 Texture を破棄してクリアする（レンダラ破棄時に呼ぶ）。 */
  dispose: () => void
  /** 現在キャッシュしているユニークキー数。 */
  readonly size: number
}

const cacheKey = (url: string, variant: boolean): string =>
  `${url}|${variant ? 1 : 0}`

export const createSharedTextureCache = (
  loadRaw: (url: string) => Promise<THREE.Texture>,
  configure: (texture: THREE.Texture, variant: boolean) => void,
): SharedTextureCache => {
  const cache = new Map<string, Promise<THREE.Texture>>()
  return {
    load(url, variant) {
      const key = cacheKey(url, variant)
      const cached = cache.get(key)
      if (cached) {
        return cached
      }
      const promise = loadRaw(url).then((texture) => {
        // 共有 Texture は個別 entry の dispose 対象から外す目印。破棄は dispose()
        // による一括解放に一本化し、ある部品の除去で他部品の参照を壊さない。
        texture.userData.shared = true
        configure(texture, variant)
        return texture
      })
      // ロード失敗（一過性のネットワークエラー等）はキャッシュに残さず、部品の再生成時に
      // 再試行できるようにする。呼び出し側が .catch でログするため握り潰しにはならない。
      promise.catch(() => {
        if (cache.get(key) === promise) {
          cache.delete(key)
        }
      })
      cache.set(key, promise)
      return promise
    },
    dispose() {
      for (const promise of cache.values()) {
        promise.then((texture) => texture.dispose()).catch(() => {})
      }
      cache.clear()
    },
    get size() {
      return cache.size
    },
  }
}
